import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { InsuranceStore } from '@sondermind/data-access/client-insurance';
import { CurrentUser } from '@sondermind/data-access/client-user';
import { θProviderNetworkCompany } from '@sondermind/utilities/models-client-facing';
import { ClientInsurance } from '@sondermind/utilities/models-client-insurance';
import { IProviderNetworkCompany } from '@sondermind/utilities/models-insurance/client';
import { IClientMatchingProfile } from '@sondermind/utilities/models-matching';
import { USStateOptions } from '@sondermind/utilities/states-list';

@Injectable({
  providedIn: 'root'
})
export class ClientCostService {
  averageClientResponsibilityInsurance: string;
  averageClientResponsibilitySelfPay: string;
  expectedCostPreDeductible: string;
  expectedCopayCost: string;
  eapPnc: θProviderNetworkCompany;
  insurancePnc: θProviderNetworkCompany;
  selfPayPnc: θProviderNetworkCompany;
  insuranceName: string = '';
  insuranceState: string = '';
  isNonEapInsurance: boolean = false;
  isEap: boolean = false;
  isSelfPay: boolean = false;
  canShowControlInsuranceCost: boolean = false;

  constructor(
    private insuranceStore: InsuranceStore,
    private currentUser: CurrentUser
  ) {}

  /**
   * Get PNC data depending from different sources depending on state of account.
   * Need to use requiresInsuranceInfo because we will show this info to both complete and incomplete accounts.
   */
  pncDataInit$(): Observable<void> {
    if (this.currentUser.user.requiresInsuranceInfo) {
      return this.getPncFromMatchingProfile$();
    }
    return this.getPncFromInsuranceStore$();
  }

  /**
   * Grab the PNC data from the stores. Set to first eapPnc and insurancePnc to
   * first value of their respective list (we only display one PNC in the help info).
   * First check regular PNC insurance list, if none exist, check list with
   * self pay PNCs included.
   *
   * NOTE: forkJoin does not work here because some lists can be empty.
   */
  getPncFromInsuranceStore$(): Observable<void> {
    return this.insuranceStore.eapList$.pipe(
      switchMap(
        (eapResp) => {
          if (eapResp.length > 0) {
            // if EAP exists, sort by most recent and use most recent
            eapResp.sort(this.pncSort);
            this.eapPnc = eapResp[0].provider_network_company;
          }

          return this.insuranceStore.insuranceList$;
        }
      ),
      switchMap(
        (insuranceResp) => {
          // if regular PNC exists, sort by most recent and use most recent
          if (insuranceResp.length > 0) {
            insuranceResp.sort(this.pncSort);

            this.insurancePnc = insuranceResp[0].provider_network_company;
          }

          return this.insuranceStore.selfPayList$;
        }
      ),
      switchMap(
        (selfPayResp) => {
          // if self pay PNC exists, find the pnc for the user's state
          if (selfPayResp.length > 0) {
            const location = this.currentUser.user.insuranceState?.length > 0
              ? this.currentUser.user.insuranceState
              : this.currentUser.matchingProfileData.locationName;
            this.selfPayPnc = this.insuranceStore.getPncByState(selfPayResp, location);
          }

          this.calculateCost();
          this.determinePaymentType();
          this.canShowControlInsuranceCost = true;

          return of(undefined);
        }
      )
    );
  }

  /**
   * If the matching profile has any providerNetworkCompanies, grab the first one and set values.
   */
  getPncFromMatchingProfile$(): Observable<void> {
    return this.currentUser.matchingProfile$.pipe(
      take(1),
      switchMap(
        (profile: IClientMatchingProfile) => {
          let pnc: IProviderNetworkCompany;

          if (profile?.providerNetworkCompanies?.length > 0) {
            // eslint-disable-next-line prefer-destructuring
            pnc = profile.providerNetworkCompanies[0];

            this.isSelfPay = pnc.isSnap;
            this.isEap = pnc.eap;
            this.isNonEapInsurance = !this.isSelfPay && !this.isEap;
            this.insuranceName = pnc.insuranceTitle;
            this.insuranceState = this.getStateName(pnc.networkState);
            this.expectedCostPreDeductible = this.stripCents(pnc.expectedClientCostPreDeductible);
            this.expectedCopayCost = this.stripCents(pnc.expectedClientCopayCost);

            if (this.isSelfPay) {
              this.averageClientResponsibilitySelfPay = this.stripCents(pnc.averageClientResponsibility);
            } else {
              this.averageClientResponsibilityInsurance = this.stripCents(pnc.averageClientResponsibility);
            }
          }

          return of(undefined);
        }
      )
    );
  }

  getStateName(networkState: string): string {
    return USStateOptions.filter((s) => networkState === s.value)[0]?.label;
  }

  /**
   * Calculate display costs from PNC data.
   */
  calculateCost(): void {
    this.insuranceName = this.insurancePnc?.insurance_name;
    this.insuranceState = this.getStateName(this.insurancePnc?.network_state);
    this.expectedCostPreDeductible = this.stripCents(this.insurancePnc?.expected_client_cost_pre_deductible);
    this.expectedCopayCost = this.stripCents(this.insurancePnc?.expected_client_copay_cost);

    if (this.insurancePnc) {
      this.averageClientResponsibilityInsurance = this.stripCents(this.insurancePnc.average_client_responsibility);
    }
    if (this.selfPayPnc) {
      this.averageClientResponsibilitySelfPay = this.stripCents(this.selfPayPnc.average_client_responsibility);
    }
  }

  /**
   * Set which payment type(s) the client has available.
   */
  determinePaymentType(): void {
    this.isSelfPay = !!this.selfPayPnc;
    this.isEap = !!this.eapPnc;
    this.isNonEapInsurance = !!this.insurancePnc;
  }

  /**
   * Remove cents value. Do not set if amount is undefined or null.
   */
  private stripCents(amount: string): string {
    if (!amount) { return amount }

    return amount.split('.')[0];
  }

  /**
   * Used to sort PNCs by most recent.
   */
  private pncSort(a: ClientInsurance, b: ClientInsurance): number {
    return Number(b.created_at) - Number(a.created_at);
  }
}
